import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, ChevronRight, Target, Flame } from 'lucide-react';
import { api } from '@/lib/api';
import { useNavigate } from 'react-router-dom';

// Prominent banner under WeeklyChallengeCard on the dashboard. Tapping opens
// the Future Me hub. Visuals: dark gradient + glow + animated ring so it
// reads as the "premium / emotional" surface in the spec.
export default function FutureMeBannerCTA() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    api.get('/future-me')
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { /* user may have no active goal — banner hides */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  if (!data?.profile) return null;
  const { profile, goal, goals, today_pending, mode } = data;
  const pct = profile.progress_percentage || 0;
  // In unified mode (default) `goal` is null and `goals` is a list; show a
  // single concise label like "3 goals" or the lone goal's title.
  const goalLabel = mode === 'unified'
    ? (goals && goals.length > 1
        ? `${goals.length} goals`
        : (goals?.[0]?.title || 'Your life vision'))
    : (goal?.title || 'Your goal');

  return (
    <motion.button
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={() => navigate('/future-me')}
      className="relative w-full rounded-3xl p-4 mt-3 mb-2 overflow-hidden text-left text-white shadow-xl active:scale-[0.99] transition-transform"
      style={{
        background: 'radial-gradient(circle at 0% 0%, #5B21B6 0%, #0F172A 60%, #000 100%)',
      }}
      data-testid="future-me-cta"
    >
      {/* Glow orbs */}
      <span className="pointer-events-none absolute -top-8 -right-8 w-32 h-32 rounded-full bg-[#7E5BEF] opacity-30 blur-3xl" />
      <span className="pointer-events-none absolute -bottom-10 left-12 w-24 h-24 rounded-full bg-[#1CB0F6] opacity-25 blur-2xl" />

      <div className="relative flex items-center gap-3">
        {/* Progress ring with the user's future-self emoji */}
        <div className="relative w-16 h-16 flex-shrink-0">
          <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
            <circle cx="32" cy="32" r="28" stroke="rgba(255,255,255,0.15)" strokeWidth="4" fill="none" />
            <circle
              cx="32" cy="32" r="28"
              stroke="url(#future-grad)" strokeWidth="4" fill="none"
              strokeDasharray={`${(pct / 100) * 176} 176`}
              strokeLinecap="round"
            />
            <defs>
              <linearGradient id="future-grad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#A78BFA" />
                <stop offset="100%" stopColor="#1CB0F6" />
              </linearGradient>
            </defs>
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-2xl" aria-hidden>✨</span>
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase font-extrabold tracking-widest opacity-80 inline-flex items-center gap-1">
            <Sparkles size={10} strokeWidth={3} /> Flagship · NEW
          </p>
          <h3 className="text-lg font-extrabold leading-tight">
            {(() => {
              // Clean common LLM artefacts: "Future Future Iter19" → "Future Iter19",
              // "Future" alone → "Future Me".
              let fn = (profile.future_name || '').trim()
                .replace(/^(future\s+)+/i, 'Future ')
                .trim();
              if (!fn || /^future$/i.test(fn)) fn = 'Future Me';
              return /^future\b/i.test(fn) ? `Visit ${fn}` : `Visit Future ${fn}`;
            })()}
          </h3>
          <p className="text-xs font-bold opacity-90 line-clamp-2 mt-0.5">
            {profile.summary || `Your Future Me is waiting. Complete today's tasks to get closer.`}
          </p>
          <div className="flex items-center gap-3 mt-2 text-[10px] font-extrabold uppercase tracking-wider">
            <span className="inline-flex items-center gap-1"><Target size={10} strokeWidth={3} /> {pct}% on path</span>
            {today_pending > 0 && (
              <span className="inline-flex items-center gap-1 text-[#FBBF24]">
                <Flame size={10} strokeWidth={3} /> {today_pending} pending today
              </span>
            )}
            <span className="opacity-70 truncate">· {goalLabel}</span>
          </div>
        </div>
        <ChevronRight size={22} strokeWidth={3} className="opacity-80 flex-shrink-0" />
      </div>
    </motion.button>
  );
}
